import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/** Same key as Sculpx Accounts — used for Socket.IO `session:revoked` matching. */
export const SESSION_ID_KEY = 'sculpx_sessionId';

export type SessionUser = {
  id?: string;
  email?: string;
  displayName?: string | null;
  [key: string]: unknown;
};

interface AuthState {
  accessToken: string | null;
  user: SessionUser | null;
}

function readToken(): string | null {
  try {
    return sessionStorage.getItem('accessToken');
  } catch {
    return null;
  }
}

function readUser(): SessionUser | null {
  try {
    const raw = sessionStorage.getItem('user');
    if (!raw) return null;
    return JSON.parse(raw) as SessionUser;
  } catch {
    return null;
  }
}

function syncSessionIdFromAccessToken(accessToken: string | null): void {
  try {
    if (!accessToken) {
      localStorage.removeItem(SESSION_ID_KEY);
      return;
    }
    const parts = accessToken.split('.');
    if (parts.length !== 3) return;
    const claims = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))) as {
      sessionId?: string;
    };
    if (claims?.sessionId) {
      localStorage.setItem(SESSION_ID_KEY, String(claims.sessionId));
    }
  } catch {
    /* ignore */
  }
}

const initialState: AuthState = {
  accessToken: readToken(),
  user: readUser(),
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    hydrateFromSession(state) {
      state.accessToken = readToken();
      state.user = readUser();
      syncSessionIdFromAccessToken(state.accessToken);
    },
    setAuth(
      state,
      action: PayloadAction<{ accessToken: string; user?: SessionUser | null }>
    ) {
      state.accessToken = action.payload.accessToken;
      state.user = action.payload.user ?? state.user;
      try {
        sessionStorage.setItem('accessToken', action.payload.accessToken);
        if (state.user) {
          sessionStorage.setItem('user', JSON.stringify(state.user));
        }
      } catch {
        /* ignore */
      }
      syncSessionIdFromAccessToken(action.payload.accessToken);
    },
    clearAuth(state) {
      state.accessToken = null;
      state.user = null;
      try {
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('user');
        localStorage.removeItem(SESSION_ID_KEY);
      } catch {
        /* ignore */
      }
    },
  },
});

export const authActions = authSlice.actions;
export default authSlice.reducer;
